import { Database } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { ExpandableText } from "@/components/ui/expandable-text"
import { ResourceField } from "@/types/api"

interface OriginalSchemaTabProps {
  fieldsData: ResourceField[] | undefined
  fieldsLoading: boolean
}

export function OriginalSchemaTab({ fieldsData, fieldsLoading }: OriginalSchemaTabProps) {
  // Skeleton loader component for fields
  const FieldSkeleton = () => (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center space-x-3">
                <div className="h-5 w-24 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
                <div className="h-5 w-16 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
                <div className="h-5 w-20 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
              </div>
              <div className="mb-2 h-4 w-32 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
              <div className="h-4 w-full animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  if (fieldsLoading) {
    return <FieldSkeleton />
  }

  if ((fieldsData || []).length === 0) {
    return (
      <div className="py-12 text-center">
        <Database className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">No fields found</h3>
        <p className="text-gray-600 dark:text-gray-400">This table doesn&apos;t have any fields yet.</p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {/* Header Row - Hidden on mobile */}
      <div className="sticky top-0 z-10 hidden grid-cols-12 items-center gap-4 rounded-lg bg-gray-50 p-3 text-sm font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400 lg:grid">
        <div className="col-span-3">FIELD NAME</div>
        <div className="col-span-3">COLUMN</div>
        <div className="col-span-3">DATA TYPE</div>
        <div className="col-span-3">ADDITIONAL INFO</div>
      </div>

      {(fieldsData || []).map((field) => (
        <div
          key={field.id}
          className="cursor-default space-y-3 rounded-lg p-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 lg:grid lg:grid-cols-12 lg:items-center lg:gap-4 lg:space-y-0"
        >
          {/* FIELD NAME */}
          <div className="min-w-0 lg:col-span-3">
            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">FIELD NAME</div>
            <ExpandableText
              text={field.display_name}
              className="font-mono text-gray-900 dark:text-gray-100"
              variant="truncate"
              threshold={28}
            />
          </div>

          {/* Column Name */}
          <div className="min-w-0 lg:col-span-3">
            <div className="mb-1 pt-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">COLUMN</div>
            <ExpandableText
              text={field.field_name}
              className="font-mono text-gray-900 dark:text-gray-100"
              variant="truncate"
              threshold={28}
            />
          </div>

          {/* Data Type */}
          <div className="min-w-0 lg:col-span-3">
            <div className="mb-1 pt-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">DATA TYPE</div>
            <ExpandableText
              text={field.data_type}
              className="font-mono text-gray-700 dark:text-gray-300"
              variant="truncate"
              threshold={28}
            />
          </div>

          {/* Additional Info */}
          <div className="lg:col-span-3">
            <div className="mb-1 pt-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">
              ADDITIONAL INFO
            </div>
            <div className="flex flex-wrap gap-1">
              {field.is_primary_key && (
                <Badge variant="outline" className="text-xs">
                  PRIMARY KEY
                </Badge>
              )}
              {field.is_foreign_key && (
                <Badge variant="outline" className="text-xs">
                  FOREIGN KEY
                </Badge>
              )}
              {!field.is_nullable && (
                <Badge variant="outline" className="text-xs">
                  NOT NULL
                </Badge>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
